/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChartOutlined } from '@ant-design/icons';
import { Outlet, useParams } from 'react-router-dom';
import { Dashboard } from './dashboard';
import { appRoutes } from 'app/routes';
import { compileMenu } from 'app/core/utils/menu';
import {
  HiMiniUsers,
  HiRectangleGroup,
  HiMiniAdjustmentsVertical,
  HiKey,
  HiMiniSignal,
  HiMiniPuzzlePiece
} from 'react-icons/hi2';

import { ImAlarm } from 'react-icons/im';
import { MdOutlineSell } from 'react-icons/md';
import { HiMiniDevicePhoneMobile } from 'react-icons/hi2';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { MdOutlineMedicationLiquid } from 'react-icons/md';
import { FaDev } from 'react-icons/fa6';

/**
 * Export `SkinUsers` component.
 */

export function SkinUsers(): JSX.Element {
  const [translate] = useTranslation();
  const params = useParams();
  const items = useMemo(
    () =>
      compileMenu([
        {
          key: 'overview',
          pathname: appRoutes.dashboard.users.base,
          icon: <PieChartOutlined />,
          label: translate('users.menu.overview')
        },
        {
          key: 'users',
          pathname: appRoutes.dashboard.users.users,
          icon: <HiMiniUsers />,
          label: translate('users.menu.users'),
          children: !params?.userId
            ? undefined
            : [
                {
                  key: 'overviewDetail',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.base, params),
                  icon: <PieChartOutlined />,
                  label: translate('users.menu.overview')
                },
                {
                  key: 'medications',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.medications, params),
                  icon: <MdOutlineMedicationLiquid />,
                  label: translate('users.menu.medications')
                },
                {
                  key: 'definedAlarms',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.definedAlarms, params),
                  icon: <ImAlarm />,
                  label: translate('users.menu.definedAlarms')
                },
                {
                  key: 'biometricData',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.biometricData, params),
                  icon: <HiRectangleGroup />,
                  label: translate('users.menu.biometricData')
                },
                {
                  key: 'settings',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.settings, params),
                  icon: <HiMiniAdjustmentsVertical />,
                  label: translate('users.menu.settings')
                },
                {
                  key: 'authorizations',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.authorizations, params),
                  icon: <HiKey />,
                  label: translate('users.menu.authorizations')
                },
                {
                  key: 'pushTokens',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.pushTokens, params),
                  icon: <HiMiniSignal />,
                  label: translate('users.menu.pushTokens')
                },
                {
                  key: 'pinNumbers',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.pinNumbers, params),
                  icon: <HiMiniPuzzlePiece />,
                  label: translate('users.menu.pinNumbers')
                },
                {
                  key: 'devices',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.devices, params),
                  icon: <HiMiniDevicePhoneMobile />,
                  label: translate('users.menu.devices')
                },
                {
                  key: 'orders',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.sales, params),
                  icon: <MdOutlineSell />,
                  label: translate('users.menu.sales')
                },
                {
                  key: 'syncAlarms',
                  pathname: resolveAppPath(appRoutes.dashboard.users.detail.devTools, params),
                  icon: <FaDev />,
                  label: translate('users.menu.devTools')
                }
              ]
        },
        {
          key: 'metrics',
          pathname: appRoutes.dashboard.users.metrics,
          icon: <PieChartOutlined />,
          label: translate('users.menu.metrics')
        }
      ]),
    [params, translate]
  );

  return (
    <Dashboard menu={items}>
      <Outlet />
    </Dashboard>
  );
}
